/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    FlexRow,
    FlexCol,
    PhoneNumberField,
    RadioGroupWithValidation,
    Radio,
    Stack,
    StatePicker,
    TextField,
    ZIPCodeField,
} from 'sarsaparilla';
import { EXPEDITED, EXPEDITED_PRICE, STANDARD, STANDARD_PRICE } from '../../constants';
import { formatPrice } from '../../utils/currency_utils';

export default function HolderForm({ errors, index, onChangeHandler, reservation }) {
    const getErrorText = (key = '') => {
        const error = errors.find((item) => {
            return item.inputId === key;
        });

        return error?.errorText || '';
    };

    return (
        <div className="ia-holder-form">
            <Stack>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            id={`first-name-${index}`}
                            label="First Name"
                            value={reservation?.pass_info?.holder_info?.first_name}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].pass_info.holder_info.first_name`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`first-name-${index}`)}
                            maxLength={100}
                            isRequired
                        />
                    </FlexCol>
                    <FlexCol lg={6}>
                        <TextField
                            id={`last-name-${index}`}
                            label="Last Name"
                            value={reservation?.pass_info?.holder_info?.last_name}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].pass_info.holder_info.last_name`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`last-name-${index}`)}
                            maxLength={100}
                            isRequired
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            id={`address-line-1-${index}`}
                            label="Address Line 1"
                            value={reservation?.shipping?.Address?.address1}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Address.address1`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`address-line-1-${index}`)}
                            maxLength={100}
                            isRequired
                        />
                    </FlexCol>
                    <FlexCol lg={6}>
                        <TextField
                            id={`address-line-2-${index}`}
                            label="Address Line 2"
                            value={reservation?.shipping?.Address?.address2}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Address.address2`,
                                    e.target.value
                                )
                            }
                            maxLength={100}
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <TextField
                            id={`city-${index}`}
                            label="City"
                            value={reservation?.shipping?.Address?.city}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Address.city`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`city-${index}`)}
                            maxLength={100}
                            isRequired
                        />
                    </FlexCol>
                    <FlexCol lg={3}>
                        <StatePicker
                            id={`state-${index}`}
                            label="State"
                            value={reservation?.shipping?.Address?.state}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Address.state`,
                                    e.target.value
                                )
                            }
                            isInvalid={!!getErrorText(`state-${index}`)}
                            errorText={getErrorText(`state-${index}`)}
                            hasArmedForces={false}
                            hasTerritories={false}
                            isRequired
                        />
                    </FlexCol>
                    <FlexCol lg={3}>
                        <ZIPCodeField
                            id={`zip-code-${index}`}
                            value={reservation?.shipping?.Address?.zip_code}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Address.zip_code`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`zip-code-${index}`)}
                            isRequired
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <PhoneNumberField
                            id={`phone-number-${index}`}
                            label="Phone Number"
                            value={reservation?.pass_info?.holder_info?.phone_number}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].pass_info.holder_info.phone_number`,
                                    e.target.value
                                )
                            }
                            errorText={getErrorText(`phone-number-${index}`)}
                            isRequired
                        />
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol lg={6}>
                        <RadioGroupWithValidation
                            id={`shipping-${index}`}
                            label="Shipping Speed"
                            isLabelVisible
                            name={`shipping-${index}`}
                            value={reservation?.shipping?.Method}
                            onChange={(e) =>
                                onChangeHandler(
                                    `[${index}].shipping.Method`,
                                    e.target.value
                                )
                            }
                        >
                            <Radio
                                id={`shipping-${index}-${STANDARD}`}
                                label={`Standard (2-3 weeks) - ${formatPrice(STANDARD_PRICE)}`}
                                value={STANDARD}
                            />
                            <Radio
                                id={`shipping-${index}-${EXPEDITED}`}
                                label={`Expedited (2-3 days) - ${formatPrice(EXPEDITED_PRICE)}`}
                                value={EXPEDITED}
                            />
                        </RadioGroupWithValidation>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol>
                        <Alert type="info">
                            <p>
                                Interagency Annual Passes cannot be delivered to a P.O.
                                Box. Please ensure that your mailing address is correct;
                                passes that are shipped to an incorrect or old address
                                cannot be replaced or refunded.
                            </p>
                            <p>
                                Delivery dates are only estimates and are not guaranteed.
                                Weather and other unforeseen circumstances may impact or
                                alter the delivery date.
                            </p>
                            <p>
                                To ship internationally, please purchase your pass using
                                the{' '}
                                <a href="https://store.usgs.gov/recreational-passes">
                                    USGS Store
                                </a>
                                .
                            </p>
                            <p>
                                Shipping and handling fees for Interagency Passes are
                                non-refundable.
                            </p>
                        </Alert>
                    </FlexCol>
                </FlexRow>
            </Stack>
        </div>
    );
}

HolderForm.propTypes = {
    errors: PropTypes.array.isRequired,
    index: PropTypes.number.isRequired,
    onChangeHandler: PropTypes.func.isRequired,
    reservation: PropTypes.object.isRequired,
};
